/**
 * React Query hook for Conference Rooms with Pagination Support
 * Designed for use with react-select-async-paginate
 * Provides automatic caching, background sync, and location-based filtering
 */

import { useQuery } from '@tanstack/react-query';
import { listConferenceRooms } from 'services/Settings';
import { queryKeys } from '../config/queryClient';

/**
 * Fetches conference rooms with location filtering
 * Note: The API doesn't support pagination tokens, so we load all rooms and paginate client-side
 *
 * @param {Object} options - Query options
 * @param {string} options.officeLocationId - Filter by office location ID
 * @param {string} options.searchQuery - Search query for filtering
 * @param {number} options.pageSize - Number of items per page (default: 20)
 * @param {number} options.page - Current page number for client-side pagination
 * @returns {Promise<Object>} - { options, hasMore, additional: { page } }
 */
const fetchConferenceRoomsPage = async ({ officeLocationId = '', searchQuery = '', pageSize = 20, page = 0 }) => {
  try {
    // Fetch both conference rooms and office locations
    const [roomsResponse] = await Promise.all([listConferenceRooms(officeLocationId)]);

    const rooms = roomsResponse?.data || [];

    // Transform to react-select format with office name
    let allOptions = rooms.map((room) => {
      const roomName = room?.room_name || room?.name || '';
      const roomId = room?.conference_room_id || room?.room_id || room?.id || '';
      const capacity = room?.capacity;

      // Include capacity in label if available
      const label = capacity ? `${roomName} (${capacity})` : roomName;

      return {
        label,
        value: roomId,
        capacity,
        office_location_id: room?.office_location_id,
        features: room?.features,
        room_name: roomName,
      };
    });

    // Client-side search filtering
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      allOptions = allOptions.filter((option) => {
        const roomName = option.room_name?.toLowerCase() || '';
        return roomName.includes(query);
      });
    }

    // Sort by room name
    allOptions.sort((a, b) => (a.room_name || '').localeCompare(b.room_name || ''));

    // Client-side pagination
    const startIndex = page * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedOptions = allOptions.slice(startIndex, endIndex);
    const hasMore = endIndex < allOptions.length;

    return {
      options: paginatedOptions,
      hasMore,
      additional: {
        page: page + 1,
      },
    };
  } catch (error) {
    console.error('Error fetching conference rooms page:', error);
    throw error;
  }
};

/**
 * Hook to fetch paginated conference rooms
 * Compatible with react-select-async-paginate
 *
 * @param {Object} params - Hook parameters
 * @param {string} params.officeLocationId - Filter by office location ID
 * @param {string} params.searchQuery - Search query for filtering
 * @param {number} params.page - Current page number (default: 0)
 * @param {number} params.pageSize - Number of items per page (default: 20)
 * @param {boolean} params.enabled - Enable/disable query (default: true)
 * @returns {Object} - { data, isLoading, error, refetch }
 */
export const useConferenceRoomsPaginated = ({ officeLocationId = '', searchQuery = '', page = 0, pageSize = 20, enabled = true } = {}) => {
  return useQuery({
    queryKey: queryKeys.conferenceRooms.paginated({
      officeLocationId,
      searchQuery,
      page,
      pageSize,
    }),
    queryFn: () =>
      fetchConferenceRoomsPage({
        officeLocationId,
        searchQuery,
        page,
        pageSize,
      }),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    keepPreviousData: true,
  });
};

/**
 * Load options function for react-select-async-paginate
 * This is the main function to use with AsyncPaginate component
 *
 * @param {string} search - Search query from user input
 * @param {Array} loadedOptions - Previously loaded options
 * @param {Object} additional - Additional data (page number)
 * @param {Object} config - Configuration options
 * @param {string} config.officeLocationId - Filter by office location ID
 * @param {number} config.pageSize - Items per page
 * @returns {Promise<Object>} - { options, hasMore, additional }
 */
export const loadConferenceRoomOptions = async (search, loadedOptions, additional, config = {}) => {
  const { officeLocationId = '', pageSize = 20 } = config;
  const page = additional?.page || 0;

  try {
    const result = await fetchConferenceRoomsPage({
      officeLocationId,
      searchQuery: search,
      page,
      pageSize,
    });

    return {
      options: result.options,
      hasMore: result.hasMore,
      additional: result.additional,
    };
  } catch (error) {
    console.error('Error loading conference room options:', error);
    return {
      options: [],
      hasMore: false,
      additional: { page: 0 },
    };
  }
};
